/**
 * Normalização de preço para o formato que o servidor aceita.
 *
 * Os campos de preço são `<input type="number">`, cujo `value` é string e pode
 * ser "" (campo limpo) ou ter mais casas do que NUMERIC(15,2) comporta. Antes
 * isso chegava cru no Postgres e voltava como
 * `invalid input syntax for type numeric` — erro de driver na cara do usuário.
 * Hoje o zod do router recusa (precoNumerico em server/routers/crmRouter.ts);
 * estas funções evitam que o request sequer saia com valor inválido.
 */

/**
 * Devolve o preço pronto para enviar, ou null quando não dá para interpretar
 * (vazio, não-numérico, negativo) — deixando o chamador decidir o que fazer.
 * Arredonda para 2 casas, que é a precisão da coluna.
 */
export function normalizePrice(raw: string | number | null | undefined): string | null {
  if (raw === null || raw === undefined) return null;
  const v = String(raw).trim();
  if (!v) return null;
  const n = Number(v);
  if (!Number.isFinite(n) || n < 0) return null;
  return n.toFixed(2);
}

/** Idem, mas com "0.00" no lugar de null — para campos onde vazio significa grátis. */
export function normalizePriceOrZero(raw: string | number | null | undefined): string {
  return normalizePrice(raw) ?? "0.00";
}

/**
 * Idem, mas aceitando o que uma pessoa realmente digita em pt-BR:
 * "R$ 1.234,56", "1.234,56", "1234,56", "1234.56", "1234".
 *
 * `normalizePrice` sozinho não serve para campos de texto livre: ele usa
 * `Number()`, que devolve NaN para "1.234,56" e — pior — lê "1.234" como
 * 1.234 (um real e vinte e três) em vez de mil duzentos e trinta e quatro.
 * Os campos de preço existentes são `<input type="number">` e escapam disso;
 * a edição inline no CRM é texto livre e não escapa.
 */
export function parseBRLInput(raw: string | number | null | undefined): string | null {
  if (raw === null || raw === undefined) return null;
  let v = String(raw).trim();
  if (!v) return null;

  // Remove símbolo de moeda, espaços (inclusive o não-quebrável do toLocaleString) e sinal de +.
  v = v.replace(/[R$\s +]/gi, "");
  if (!v) return null;

  const hasComma = v.includes(",");
  const hasDot = v.includes(".");

  if (hasComma) {
    // Vírgula presente = separador decimal pt-BR; ponto só pode ser milhar.
    v = v.replace(/\./g, "").replace(",", ".");
  } else if (hasDot) {
    // Só ponto: ambíguo. "1.234" é milhar; "1.23" e "1.2" são decimais.
    // Vários pontos ("1.234.567") também é sempre milhar.
    const parts = v.split(".");
    const isThousandGrouping =
      parts.length > 2 || (parts.length === 2 && parts[1].length === 3);
    if (isThousandGrouping) v = parts.join("");
  }

  return normalizePrice(v);
}
